import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import Header from '../../components/ui/Header';
import MobileBottomNav from '../../components/ui/MobileBottomNav';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import StatsCard from '../admin-dashboard/components/StatsCard';
import TenantDirectoryTable from './components/TenantDirectoryTable';

import SuspensionControlPanel from './components/SuspensionControlPanel';
import CriticalAlertsPanel from './components/CriticalAlertsPanel';

const SuperAdminTenantManagement = () => {
  const navigate = useNavigate();
  const { user, isSuperAdmin, loading: authLoading } = useAuth();
  const [stats, setStats] = useState({
    totalTenants: 0,
    activeTenants: 0,
    suspendedTenants: 0,
    overduePayments: 0,
    totalOutstanding: 0,
    criticalAlerts: 0
  });
  const [tenants, setTenants] = useState([]);
  const [billingStatuses, setBillingStatuses] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [showSuspensionModal, setShowSuspensionModal] = useState(false);

  useEffect(() => {
    if (!authLoading && (!user || !isSuperAdmin)) {
      navigate('/login');
    }
  }, [user, isSuperAdmin, authLoading, navigate]);

  useEffect(() => {
    if (isSuperAdmin) {
      loadTenantData();
    }
  }, [isSuperAdmin]);

  const loadTenantData = async () => {
    setLoading(true);
    setError('');

    try {
      // Load all tenants
      const { data: tenantsData, error: tenantsError } = await supabase
        ?.from('tenants')
        ?.select(`
          *,
          user_profiles:owner_id(id, full_name, email)
        `)
        ?.order('created_at', { ascending: false });

      if (tenantsError) throw tenantsError;

      // Load billing statuses
      const { data: billingData, error: billingError } = await supabase
        ?.from('tenant_billing_status')
        ?.select('*');

      if (billingError) throw billingError;

      // Load suspension history
      const { data: suspensionsData, error: suspensionsError } = await supabase
        ?.from('tenant_suspensions')
        ?.select('*')
        ?.eq('is_active', true);

      if (suspensionsError) throw suspensionsError;

      // Merge data
      const enrichedTenants = tenantsData?.map(tenant => {
        const billing = billingData?.find(b => b?.tenant_id === tenant?.id);
        const suspension = suspensionsData?.find(s => s?.tenant_id === tenant?.id);
        return {
          ...tenant,
          billing_status: billing,
          active_suspension: suspension
        };
      });

      setTenants(enrichedTenants || []);
      setBillingStatuses(billingData || []);

      // Calculate stats
      const activeTenants = tenantsData?.filter(t => t?.status === 'active')?.length || 0;
      const suspendedTenants = tenantsData?.filter(t => t?.status === 'suspended')?.length || 0;
      const overduePayments = billingData?.filter(b => b?.is_overdue)?.length || 0;
      const totalOutstanding = billingData?.reduce((sum, b) => sum + parseFloat(b?.outstanding_balance || 0), 0);

      // Generate alerts
      const alertsList = [];

      if (overduePayments > 0) {
        alertsList?.push({
          id: 'overdue-payments',
          type: 'error',
          title: 'Overdue Payments',
          message: `${overduePayments} gym location${overduePayments > 1 ? 's' : ''} with overdue payments`,
          priority: 'critical',
          count: overduePayments
        });
      }

      if (suspendedTenants > 0) {
        alertsList?.push({
          id: 'suspended-tenants',
          type: 'warning',
          title: 'Suspended Locations',
          message: `${suspendedTenants} gym location${suspendedTenants > 1 ? 's are' : ' is'} currently suspended`,
          priority: 'high',
          count: suspendedTenants
        });
      }

      const highFailureCount = billingData?.filter(b => b?.failed_payment_count >= 3)?.length || 0;
      if (highFailureCount > 0) {
        alertsList?.push({
          id: 'high-failure-count',
          type: 'warning',
          title: 'Multiple Payment Failures',
          message: `${highFailureCount} location${highFailureCount > 1 ? 's' : ''} with 3+ failed payment attempts`,
          priority: 'high',
          count: highFailureCount
        });
      }

      setAlerts(alertsList);
      setStats({
        totalTenants: tenantsData?.length || 0,
        activeTenants,
        suspendedTenants,
        overduePayments,
        totalOutstanding,
        criticalAlerts: alertsList?.length || 0
      });
    } catch (err) {
      setError(err?.message || 'Failed to load tenant data');
      console.error('Tenant data load error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSuspendTenant = (tenant) => {
    setSelectedTenant(tenant);
    setShowSuspensionModal(true);
  };

  const handleReactivateTenant = async (tenantId) => {
    try {
      // Update tenant status
      const { error: updateError } = await supabase
        ?.from('tenants')
        ?.update({ status: 'active', updated_at: new Date()?.toISOString() })
        ?.eq('id', tenantId);

      if (updateError) throw updateError;

      // Mark suspension as inactive
      const { error: suspensionError } = await supabase
        ?.from('tenant_suspensions')
        ?.update({
          is_active: false,
          reactivated_at: new Date()?.toISOString(),
          reactivated_by: user?.id,
          reactivation_notes: 'Reactivated by super administrator'
        })
        ?.eq('tenant_id', tenantId)
        ?.eq('is_active', true);

      if (suspensionError) throw suspensionError;

      // Reload data
      await loadTenantData();
    } catch (err) {
      setError(err?.message || 'Failed to reactivate tenant');
    }
  };

  const filteredTenants = tenants?.filter(tenant => {
    const matchesStatus = filterStatus === 'all' || tenant?.status === filterStatus;
    const matchesSearch = !searchQuery || 
      tenant?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
      tenant?.email?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center h-[calc(100vh-80px)]">
          <div className="text-center">
            <Icon name="Loader2" size={48} className="animate-spin text-primary mx-auto mb-4" />
            <p className="text-muted-foreground">Loading tenant management...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="container mx-auto px-4 py-6 pb-24 md:pb-6">
        {/* Page Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <h1 className="text-3xl font-heading font-bold text-foreground flex items-center gap-3">
              <Icon name="Building2" size={32} color="var(--color-primary)" />
              Super Admin Tenant Management
            </h1>
            <Button onClick={loadTenantData} variant="outline">
              <Icon name="RefreshCw" size={16} />
              Refresh
            </Button>
          </div>
          <p className="text-muted-foreground">Comprehensive oversight and control of all gym locations</p>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-destructive/10 border border-destructive rounded-lg flex items-center gap-3">
            <Icon name="AlertCircle" size={20} color="var(--color-destructive)" />
            <p className="text-destructive">{error}</p>
          </div>
        )}

        {/* Critical Alerts */}
        {alerts?.length > 0 && (
          <CriticalAlertsPanel alerts={alerts} onRefresh={loadTenantData} />
        )}

        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-6">
          <StatsCard
            title="Total Locations"
            value={stats?.totalTenants}
            icon="Building2"
            iconColor="var(--color-primary)"
            color="primary"
            subtitle=""
            trend=""
            trendValue={0}
          />
          <StatsCard
            title="Active"
            value={stats?.activeTenants}
            icon="CheckCircle"
            iconColor="var(--color-success)"
            color="success"
            subtitle=""
            trend=""
            trendValue={0}
          />
          <StatsCard
            title="Suspended"
            value={stats?.suspendedTenants}
            icon="XCircle"
            iconColor="var(--color-destructive)"
            color="destructive"
            subtitle=""
            trend=""
            trendValue={0}
          />
          <StatsCard
            title="Overdue Payments"
            value={stats?.overduePayments}
            icon="AlertTriangle"
            iconColor="var(--color-warning)"
            color="warning"
            subtitle=""
            trend=""
            trendValue={0}
          />
          <StatsCard
            title="Outstanding Balance"
            value={`${stats?.totalOutstanding?.toFixed(2)}`}
            icon="DollarSign"
            iconColor="var(--color-primary)"
            color="primary"
            subtitle=""
            trend=""
            trendValue={0}
          />
          <StatsCard
            title="Critical Alerts"
            value={stats?.criticalAlerts}
            icon="Bell"
            iconColor="var(--color-destructive)"
            color="destructive"
            subtitle=""
            trend=""
            trendValue={0}
          />
        </div>

        {/* Filters */}
        <div className="mb-6 flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Icon name="Search" size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search by gym name or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e?.target?.value)}
                className="w-full pl-10 pr-4 py-2 border border-border rounded-lg bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              variant={filterStatus === 'all' ? 'default' : 'outline'}
              onClick={() => setFilterStatus('all')}
            >
              All
            </Button>
            <Button
              variant={filterStatus === 'active' ? 'default' : 'outline'}
              onClick={() => setFilterStatus('active')}
            >
              Active
            </Button>
            <Button
              variant={filterStatus === 'suspended' ? 'default' : 'outline'}
              onClick={() => setFilterStatus('suspended')}
            >
              Suspended
            </Button>
            <Button
              variant={filterStatus === 'trial' ? 'default' : 'outline'}
              onClick={() => setFilterStatus('trial')}
            >
              Trial
            </Button>
          </div>
        </div>

        {/* Tenant Directory Table */}
        <TenantDirectoryTable
          tenants={filteredTenants}
          onSuspend={handleSuspendTenant}
          onReactivate={handleReactivateTenant}
          onViewDetails={(tenant) => navigate(`/tenant-billing-suspension-dashboard?tenant=${tenant?.id}`)}
        />

        {/* Suspension Control Modal */}
        {showSuspensionModal && selectedTenant && (
          <SuspensionControlPanel
            tenant={selectedTenant}
            onClose={() => {
              setShowSuspensionModal(false);
              setSelectedTenant(null);
            }}
            onSuccess={() => {
              setShowSuspensionModal(false);
              setSelectedTenant(null);
              loadTenantData();
            }}
          />
        )}
      </main>

      <MobileBottomNav />
    </div>
  );
};

export default SuperAdminTenantManagement;